import { motion } from "framer-motion";
import { ArrowUpRight, ExternalLink } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api, SOCIALS } from "@/lib/api";

export default function Gallery() {
  const { data: rawItems = [], isLoading: loading } = useQuery({
    queryKey: ["behance"],
    queryFn: async () => (await api.get("/behance")).data,
  });
  const items = rawItems.slice(0, 8);

  return (
    <section id="work" className="relative py-28 md:py-40">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="text-[11px] uppercase tracking-[0.4em] text-gold">Selected Work</span>
              <span className="h-px w-16 bg-[#264738]" />
            </div>
            <h2 className="font-display text-ivory text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              From the <span className="italic text-gold">Behance</span> archive.
            </h2>
          </div>
          <a
            href={SOCIALS.behance}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-3 text-ivory hover:text-gold text-[11px] uppercase tracking-[0.3em] group"
            data-testid="behance-view-all"
          >
            View Full Portfolio
            <ExternalLink size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
          </a>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {["sk-a","sk-b","sk-c","sk-d","sk-e","sk-f"].map((k, i) => (
              <div key={k} className={`bg-[#162E24] animate-pulse ${i === 0 ? "md:col-span-8 aspect-[4/3]" : "md:col-span-4 aspect-square"}`} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyGallery />
        ) : (
          <BentoGrid items={items} />
        )}
      </div>
    </section>
  );
}

function BentoGrid({ items }) {
  // Build a bento layout: first item large, alternating
  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
      {items.map((it, i) => {
        const span = layoutFor(i);
        return (
          <motion.a
            key={it.id}
            href={it.url}
            target="_blank"
            rel="noreferrer"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: (i % 4) * 0.08 }}
            className={`relative overflow-hidden group block ${span.cls} bg-[#162E24]`}
            data-testid="behance-gallery-item"
          >
            {it.cover_image ? (
              <img
                src={it.cover_image}
                alt={it.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
            ) : (
              <div className="absolute inset-0 bg-gradient-to-br from-[#162E24] to-[#0F211A] flex items-center justify-center">
                <span className="font-display text-gold text-xl">{it.title.slice(0, 24)}</span>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F211A] via-[#0F211A]/30 to-transparent opacity-70 group-hover:opacity-90 transition-opacity duration-500" />
            <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end">
              <div className="flex items-end justify-between gap-4 translate-y-2 group-hover:translate-y-0 transition-transform duration-500">
                <div>
                  {it.categories?.[0] && (
                    <span className="text-[10px] uppercase tracking-[0.3em] text-gold mb-2 inline-block">
                      {it.categories[0]}
                    </span>
                  )}
                  <h3 className="font-display text-ivory text-lg md:text-2xl leading-snug">{it.title}</h3>
                </div>
                <ArrowUpRight className="text-ivory shrink-0 group-hover:text-gold group-hover:rotate-45 transition-all duration-500" size={22} />
              </div>
            </div>
          </motion.a>
        );
      })}
    </div>
  );
}

function layoutFor(i) {
  // Repeating bento pattern of 4 items per row block
  const pattern = [
    { cls: "md:col-span-8 aspect-[16/10]" },
    { cls: "md:col-span-4 aspect-[4/5]" },
    { cls: "md:col-span-4 aspect-[4/5]" },
    { cls: "md:col-span-8 aspect-[16/10]" },
  ];
  return pattern[i % pattern.length];
}

function EmptyGallery() {
  return (
    <div className="border border-[#264738] py-20 px-6 text-center">
      <p className="text-ivory-muted text-base mb-6">
        Live portfolio loading from Behance — pinned projects appear here automatically.
      </p>
      <a
        href={SOCIALS.behance}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-2 text-gold border border-gold/40 hover:bg-gold hover:text-forest px-6 py-3 text-[11px] uppercase tracking-[0.3em] transition-colors"
      >
        Visit behance.net/styleepix
        <ExternalLink size={14} />
      </a>
    </div>
  );
}
